/**
 * outbox — une file d'envoi de fichiers où rien ne se perd en silence.
 *
 * Née pour l'enregistreur vocal (`engine/voice`), généralisée pour la copie d'une activité (photo par
 * photo et oral, `engine/activity`) : un fichier confié à la file a un état visible, `pending` (à
 * envoyer), `sending` (en cours), `sent` (envoyé, avec la réponse), `failed` (échec, et `retry` le
 * remet à envoyer). La file survit à l'app tuée : elle garde une copie durable du fichier
 * (`OutboxFileStore`) et sa référence, persistée (`KeyValueStorage`) ; à la relance, ce qui attendait
 * repart, et ce qui était EN COURS d'envoi passe en échec plutôt que de repartir seul — le serveur a pu
 * le recevoir, et un « Réessayer » vaut mieux qu'un envoi en double.
 *
 * L'envoi est un paramètre (`send`) : la file ne connaît ni point d'API, ni rôle, ni écran. La cible
 * (`target` : un fil, une étape d'activité…) est opaque pour elle. Un envoi en plusieurs temps (S3,
 * puis le journal) écrit où il en est (`saveProgress`) : cette avancée est persistée avec l'élément, et
 * un « Réessayer » la retrouve — l'étape déjà faite ne se refait pas. Un envoi à la fois ; un même
 * élément n'est jamais envoyé deux fois en même temps.
 *
 * Une file peut garder le compte qui confie (`owner`, facultatif) : l'appareil se partage (un frère,
 * une sœur, un autre élève), et un envoi parti sous le compte connecté au moment du renvoi serait
 * écrit chez un autre. Avec un propriétaire, l'élément porte le compte qui l'a confié ; sous un autre
 * compte, il ne part pas, n'est pas montré (`getState` ne rend que ceux du compte connecté) et ne se
 * relance ni ne se retire : il reste à son compte, et part quand celui-ci revient.
 *
 * Deux couches : un réducteur pur (`outboxReducer`, testé en rejouant des suites d'événements) et un
 * magasin (`createOutbox`) qui branche le stockage, les fichiers et l'envoi ; un écran le lit par
 * `useOutbox`.
 */
import { useSyncExternalStore } from "react"

import { TransitionTrail } from "../telemetryMetadata"
import { createOutboxFileStore, type OutboxFileStore } from "./outboxFileStore"
import { defaultKeyValueStorage, type KeyValueStorage } from "./outboxStorage"

export type { KeyValueStorage, OutboxFileStore }

/** Un fichier fini, tel que la file le garde. */
export type OutboxFile = {
    /** L'adresse locale (`file://…` en natif, `blob:` ou `data:` sur le web). */
    uri: string
    /** Le vrai type, sans paramètres (`audio/mp4`, `image/jpeg`). */
    mimeType: string
    /** `.m4a`, `.jpg`… */
    extension: string
}

export type OutboxStatus = "pending" | "sending" | "sent" | "failed"

/** Le compte qui confie un élément (l'id de l'élève connecté). */
export type OutboxOwner = string | number

/** Où en est un envoi en plusieurs temps : des valeurs simples, persistées avec l'élément. */
export type OutboxProgress = Record<string, string | number | null>

export type OutboxItem<Target, Result = unknown, File extends OutboxFile = OutboxFile> = {
    id: string
    /** ISO, l'heure où il a été confié à la file. */
    createdAt: string
    file: File
    target: Target
    status: OutboxStatus
    /** Les envois tentés. */
    attempts: number
    /** Le dernier échec : `file_missing`, `interrupted`, ou le message de l'envoi. */
    error: string | null
    /** La réponse de l'envoi, une fois `sent` (jamais persistée). */
    result: Result | null
    /** La copie durable du fichier est faite : il peut partir. */
    kept: boolean
    /** L'étape déjà faite d'un envoi en plusieurs temps (la clé S3…), ou `null`. */
    progress: OutboxProgress | null
    /** Le compte qui l'a confié, dans une file qui le garde ; absent ou `null` : il part sous tout compte. */
    owner?: OutboxOwner | null
}

export type OutboxState<Target, Result = unknown, File extends OutboxFile = OutboxFile> = {
    /** La file persistée est relue. */
    hydrated: boolean
    items: OutboxItem<Target, Result, File>[]
}

export type OutboxEvent<Target, Result = unknown, File extends OutboxFile = OutboxFile> =
    | { type: "HYDRATED"; items: OutboxItem<Target, Result, File>[] }
    | { type: "ENQUEUED"; item: OutboxItem<Target, Result, File> }
    | { type: "FILE_KEPT"; id: string; file: File }
    | { type: "SEND_STARTED"; id: string }
    | { type: "PROGRESSED"; id: string; progress: OutboxProgress }
    | { type: "SEND_SUCCEEDED"; id: string; result: Result }
    | { type: "SEND_FAILED"; id: string; error: string }
    | { type: "RETRY"; id: string }
    | { type: "REMOVED"; id: string }

/** L'échec d'un envoi interrompu par l'app tuée : il ne repart que sur un « Réessayer ». */
export const INTERRUPTED = "interrupted"
/** Le fichier n'est plus là (cache vidé, stockage du navigateur effacé). */
export const FILE_MISSING = "file_missing"

export function initialOutboxState<T, R = unknown, F extends OutboxFile = OutboxFile>(): OutboxState<T, R, F> {
    return { hydrated: false, items: [] }
}

function patch<T, R, F extends OutboxFile>(
    items: OutboxItem<T, R, F>[],
    id: string,
    change: (item: OutboxItem<T, R, F>) => OutboxItem<T, R, F> | null
) {
    let changed = false
    const next = items.map((item) => {
        if (item.id !== id) return item
        const updated = change(item)
        if (updated == null || updated === item) return item
        changed = true
        return updated
    })
    return changed ? next : items
}

/** Ce que la file relue devient : un envoi interrompu passe en échec ; un envoi fini n'est pas gardé. */
export function reviveItem<T, R, F extends OutboxFile>(item: OutboxItem<T, R, F>): OutboxItem<T, R, F> | null {
    const revived = { ...item, result: null, progress: item.progress ?? null }
    if (item.status === "sent") return null
    if (item.status === "sending") return { ...revived, status: "failed", error: INTERRUPTED }
    return revived
}

export function outboxReducer<T, R, F extends OutboxFile>(
    state: OutboxState<T, R, F>,
    event: OutboxEvent<T, R, F>
): OutboxState<T, R, F> {
    const patched = (change: (item: OutboxItem<T, R, F>) => OutboxItem<T, R, F> | null) => {
        if (!("id" in event)) return state
        const items = patch(state.items, event.id, change)
        return items === state.items ? state : { ...state, items }
    }
    switch (event.type) {
        case "HYDRATED": {
            // Ce qui a été confié pendant la relecture reste, après ce qui était gardé.
            const revived = event.items.map(reviveItem).filter((item): item is OutboxItem<T, R, F> => item != null)
            const known = new Set(revived.map((item) => item.id))
            return { hydrated: true, items: [...revived, ...state.items.filter((item) => !known.has(item.id))] }
        }
        case "ENQUEUED":
            if (state.items.some((item) => item.id === event.item.id)) return state
            return { ...state, items: [...state.items, event.item] }
        case "FILE_KEPT":
            return patched((item) => ({ ...item, file: event.file, kept: true }))
        case "SEND_STARTED":
            return patched((item) =>
                item.status === "pending" && item.kept
                    ? { ...item, status: "sending", attempts: item.attempts + 1, error: null }
                    : null
            )
        case "PROGRESSED":
            return patched((item) =>
                item.status === "sending" ? { ...item, progress: { ...item.progress, ...event.progress } } : null
            )
        case "SEND_SUCCEEDED":
            return patched((item) =>
                item.status === "sending" ? { ...item, status: "sent", result: event.result, error: null } : null
            )
        case "SEND_FAILED":
            return patched((item) =>
                item.status === "sending" ? { ...item, status: "failed", error: event.error } : null
            )
        case "RETRY":
            return patched((item) => (item.status === "failed" ? { ...item, status: "pending", error: null } : null))
        case "REMOVED":
            if (!state.items.some((item) => item.id === event.id)) return state
            return { ...state, items: state.items.filter((item) => item.id !== event.id) }
        default:
            return state
    }
}

/**
 * L'élément est au compte connecté. `connected` absent : la file ne garde pas de compte. Un élément
 * sans propriétaire (une ligne d'avant la garde) est à tous.
 */
export function belongsTo(item: { owner?: OutboxOwner | null }, connected?: OutboxOwner | null): boolean {
    if (connected === undefined || item.owner == null) return true
    return connected != null && String(item.owner) === String(connected)
}

/** Le prochain à partir : le plus ancien qui attend, dont le fichier est gardé, et du compte connecté. */
export function nextToSend<T, R, F extends OutboxFile>(
    state: OutboxState<T, R, F>,
    connected?: OutboxOwner | null
): OutboxItem<T, R, F> | null {
    if (!state.hydrated || state.items.some((item) => item.status === "sending")) return null
    return state.items.find((item) => item.status === "pending" && item.kept && belongsTo(item, connected)) ?? null
}

/** Ce qui se persiste : tout sauf ce qui est parti, sans la réponse. */
export function persistedItems<T, R, F extends OutboxFile>(state: OutboxState<T, R, F>): OutboxItem<T, R, F>[] {
    return state.items.filter((item) => item.status !== "sent").map((item) => ({ ...item, result: null }))
}

/* ─────────────────────────────── la télémétrie ─────────────────────────────── */

/** Ce qu'une file dit à la télémétrie : un fil d'Ariane par transition, une erreur typée par échec. */
export type OutboxTelemetryMetadata = Record<string, string | number | boolean | null | undefined | string[]>

export type OutboxTelemetry = {
    breadcrumb: (message: string, metadata?: OutboxTelemetryMetadata) => void
    error: (error: Error, metadata?: OutboxTelemetryMetadata) => void
}

/** Une erreur de la file : l'envoi a échoué, le fichier n'est plus là, la copie ou le stockage a raté. */
export class OutboxError extends Error {
    readonly kind: "send_failed" | "file_missing" | "keep_failed" | "storage_failed"
    readonly cause: unknown
    constructor(kind: OutboxError["kind"], cause: unknown, name = "OutboxError") {
        super(cause instanceof Error ? cause.message : String(cause ?? kind))
        this.name = name
        this.kind = kind
        this.cause = cause
        if (cause instanceof Error && cause.stack) this.stack = cause.stack
    }
}

/** Sans branchement, les fils d'Ariane ne vont nulle part et les erreurs vont à la console. */
const consoleTelemetry: OutboxTelemetry = {
    breadcrumb: () => {},
    error: (error, metadata) => console.warn(`[outbox] ${error.name}: ${error.message}`, metadata ?? {}),
}

/* ─────────────────────────────── le magasin ─────────────────────────────── */

export type OutboxSendContext = {
    /** Écrit l'étape faite d'un envoi en plusieurs temps : persistée avant que l'envoi continue. */
    saveProgress: (progress: OutboxProgress) => Promise<void>
}

export type OutboxOptions<Target, Result, File extends OutboxFile = OutboxFile> = {
    /** La clé de la file persistée. Une file par usage (la messagerie, la copie d'une activité…). */
    storageKey: string
    /** L'envoi : rend la réponse, ou rejette. */
    send: (item: OutboxItem<Target, Result, File>, context: OutboxSendContext) => Promise<Result>
    /** Où la copie durable des fichiers se range (`createOutboxFileStore(nom)`). */
    fileStore: OutboxFileStore<File>
    storage?: KeyValueStorage
    /** Le nom des fils d'Ariane (`voice.outbox`, `activity.copy`). */
    name?: string
    telemetry?: OutboxTelemetry
    /** Le nom de ses erreurs (`VoiceOutboxError`…). Défaut `OutboxError`. */
    errorName?: string
    newId?: () => string
    /**
     * Le compte connecté, lu à chaque geste : la file garde alors le compte qui confie (voir l'en-tête).
     * Absent : pas de garde.
     */
    owner?: () => OutboxOwner | null
}

export type Outbox<Target, Result = unknown, File extends OutboxFile = OutboxFile> = {
    /** L'état, réduit aux éléments du compte connecté quand la file le garde. */
    getState: () => OutboxState<Target, Result, File>
    subscribe: (listener: () => void) => () => void
    /** Confie un fichier à la file ; rend son id. Le même fichier confié deux fois n'en fait qu'un. */
    enqueue: (file: File, target: Target) => string
    /** Un échec repart (`failed` → `pending`). */
    retry: (id: string) => void
    /** Retire un élément et sa copie du fichier (pas pendant son envoi). */
    remove: (id: string) => void
    /** Résolu quand la file persistée est relue (la relit au besoin). */
    ready: () => Promise<void>
    /** Résolu quand plus rien n'est en cours ni à envoyer (pour les tests). */
    idle: () => Promise<void>
}

let sequence = 0
const defaultNewId = () => `outbox-${Date.now().toString(36)}-${(++sequence).toString(36)}`

/** Une ligne relue, vérifiée : la file persistée peut venir d'une autre version de l'app. */
function isItem(value: unknown): value is OutboxItem<unknown> {
    const item = value as OutboxItem<unknown> | null
    return (
        item != null &&
        typeof item.id === "string" &&
        typeof item.file?.uri === "string" &&
        typeof item.file?.mimeType === "string" &&
        typeof item.status === "string"
    )
}

export function createOutbox<Target, Result = unknown, File extends OutboxFile = OutboxFile>(
    options: OutboxOptions<Target, Result, File>
): Outbox<Target, Result, File> {
    const storage = options.storage ?? defaultKeyValueStorage
    const fileStore = options.fileStore
    const newId = options.newId ?? defaultNewId
    const name = options.name ?? "outbox"
    const telemetry = options.telemetry ?? consoleTelemetry
    const errorName = options.errorName ?? "OutboxError"
    const trail = new TransitionTrail()
    const report = (kind: OutboxError["kind"], cause: unknown, extra: Record<string, string | number> = {}) =>
        telemetry.error(new OutboxError(kind, cause, errorName), { transitions: trail.list(), ...extra })

    let state = initialOutboxState<Target, Result, File>()
    /** Le compte connecté ; `undefined` quand la file ne garde pas de compte. */
    const connected = (): OutboxOwner | null | undefined => (options.owner != null ? options.owner() : undefined)
    // L'état vu d'un écran, refait seulement quand l'état ou le compte change (`useSyncExternalStore`
    // attend le même objet tant que rien n'a changé).
    let view: { source: OutboxState<Target, Result, File>; owner: OutboxOwner | null | undefined } | null = null
    let visible = state
    const visibleState = () => {
        const owner = connected()
        if (owner === undefined) return state
        if (view?.source !== state || view.owner !== owner) {
            view = { source: state, owner }
            visible = state.items.every((item) => belongsTo(item, owner))
                ? state
                : { ...state, items: state.items.filter((item) => belongsTo(item, owner)) }
        }
        return visible
    }
    /** Un élément du compte connecté (l'écran ne voit, ne relance et ne retire que ceux-là). */
    const own = (id: string) => {
        const item = state.items.find((candidate) => candidate.id === id)
        return item != null && belongsTo(item, connected()) ? item : null
    }
    const listeners = new Set<() => void>()
    let hydration: Promise<void> | null = null
    let persisting: Promise<void> = Promise.resolve()
    let pumping: Promise<void> | null = null
    const keeps = new Set<Promise<void>>()

    const dispatch = (event: OutboxEvent<Target, Result, File>) => {
        const previous = state
        state = outboxReducer(previous, event)
        if (state === previous) return false
        const subject = "id" in event ? state.items.find((item) => item.id === event.id) : undefined
        const transition = `${event.type}${subject != null ? ` → ${subject.status}` : ""}`
        trail.push(transition)
        telemetry.breadcrumb(name, { transition, ...(subject != null ? { attempts: subject.attempts } : {}) })
        listeners.forEach((listener) => listener())
        return true
    }

    const persist = () => {
        const snapshot = JSON.stringify(persistedItems(state))
        persisting = persisting
            .then(() => storage.setItem(options.storageKey, snapshot))
            .catch((error) => {
                report("storage_failed", error)
            })
        return persisting
    }

    const hydrate = () => {
        if (hydration != null) return hydration
        hydration = (async () => {
            let rows: unknown[] = []
            try {
                const raw = await storage.getItem(options.storageKey)
                const parsed = raw != null ? JSON.parse(raw) : []
                rows = Array.isArray(parsed) ? parsed : []
            } catch (error) {
                report("storage_failed", error)
            }
            const items: OutboxItem<Target, Result, File>[] = []
            for (const row of rows.filter(isItem) as OutboxItem<Target, Result, File>[]) {
                // Un fichier absent rend `null` ; un fichier qui ne se relit pas rejette : sa cause part avec l'échec.
                let unreadable: unknown = null
                const file = await fileStore.resolve(row.id, row.file).catch((error: unknown) => {
                    unreadable = error
                    return null
                })
                if (file == null) {
                    report("file_missing", unreadable ?? FILE_MISSING)
                    items.push({ ...row, kept: true, status: "failed", error: FILE_MISSING })
                } else {
                    items.push({ ...row, file, kept: true })
                }
            }
            dispatch({ type: "HYDRATED", items })
            await persist()
            void pump()
        })()
        return hydration
    }

    const sendOne = async (item: OutboxItem<Target, Result, File>) => {
        if (!dispatch({ type: "SEND_STARTED", id: item.id })) return
        // L'état « en cours » est écrit AVANT l'envoi : une app tuée pendant l'envoi le relira en échec.
        await persist()
        const sending = state.items.find((candidate) => candidate.id === item.id) ?? item
        const context: OutboxSendContext = {
            saveProgress: async (progress) => {
                dispatch({ type: "PROGRESSED", id: item.id, progress })
                await persist()
            },
        }
        try {
            const result = await options.send(sending, context)
            dispatch({ type: "SEND_SUCCEEDED", id: item.id, result })
            await fileStore
                .release(item.id, item.file)
                .catch((error) =>
                    telemetry.breadcrumb(`${name}_release_failed`, { reason: (error as Error)?.message ?? "" })
                )
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error)
            report("send_failed", error, { attempts: sending.attempts, mimeType: item.file.mimeType })
            dispatch({ type: "SEND_FAILED", id: item.id, error: message })
        }
        await persist()
    }

    function pump(): Promise<void> {
        if (pumping != null) return pumping
        pumping = (async () => {
            for (let next = nextToSend(state, connected()); next != null; next = nextToSend(state, connected())) {
                await sendOne(next)
            }
        })().finally(() => {
            pumping = null
            // Un élément confié pendant le dernier envoi part à son tour.
            if (nextToSend(state, connected()) != null) void pump()
        })
        return pumping
    }

    const keep = async (id: string, file: File) => {
        let kept = file
        try {
            kept = await fileStore.keep(id, file)
        } catch (error) {
            // Sans copie durable, il part quand même : seule la survie à l'app tuée est perdue.
            report("keep_failed", error)
        }
        dispatch({ type: "FILE_KEPT", id, file: kept })
        await persist()
        void pump()
    }

    return {
        getState: visibleState,
        subscribe: (listener) => {
            listeners.add(listener)
            // Un écran qui s'ouvre relance la file : ce qui attendait son compte part quand il revient.
            void hydrate().then(() => pump())
            return () => listeners.delete(listener)
        },
        enqueue: (file, target) => {
            const owner = connected()
            const existing = state.items.find(
                (item) => item.file.uri === file.uri && item.status !== "sent" && belongsTo(item, owner)
            )
            if (existing != null) return existing.id
            const id = newId()
            dispatch({
                type: "ENQUEUED",
                item: {
                    id,
                    createdAt: new Date().toISOString(),
                    file,
                    target,
                    status: "pending",
                    attempts: 0,
                    error: null,
                    result: null,
                    kept: false,
                    progress: null,
                    ...(owner !== undefined ? { owner } : {}),
                },
            })
            const keeping = hydrate().then(() => keep(id, file))
            keeps.add(keeping)
            void keeping.finally(() => keeps.delete(keeping))
            return id
        },
        retry: (id) => {
            if (own(id) != null && dispatch({ type: "RETRY", id })) void persist().then(() => pump())
        },
        remove: (id) => {
            const item = own(id)
            if (item == null || item.status === "sending") return
            dispatch({ type: "REMOVED", id })
            void persist()
            void fileStore
                .release(id, item.file)
                .catch((error) =>
                    telemetry.breadcrumb(`${name}_release_failed`, { reason: (error as Error)?.message ?? "" })
                )
        },
        ready: () => hydrate(),
        idle: async () => {
            await hydrate()
            await Promise.all([...keeps])
            while (pumping != null || nextToSend(state, connected()) != null) {
                await (pumping ?? pump())
            }
            await persisting
        },
    }
}

/** La file vue d'un écran : son état, relu à chaque changement. */
export function useOutbox<Target, Result, File extends OutboxFile>(
    outbox: Outbox<Target, Result, File>
): OutboxState<Target, Result, File> {
    return useSyncExternalStore(outbox.subscribe, outbox.getState, outbox.getState)
}

export { createOutboxFileStore }
